import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronsUpDown, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { tokens, matches, highlightRanges, suggest } from '@/lib/justoSearch';

const Highlight = ({ text, toks }) => (
  <>{highlightRanges(text, toks).map((p, i) => (p.hit ? <mark key={i} className="bg-orange-200 text-slate-900 rounded-sm">{p.text}</mark> : <span key={i}>{p.text}</span>))}</>
);

const Option = ({ ing, toks, active, selected, onPick, onHover, testId, optRef }) => (
  <button type="button" ref={optRef} onMouseDown={(e) => { e.preventDefault(); onPick(ing); }} onMouseEnter={onHover}
    className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-sm ${active ? 'bg-orange-50' : ''} ${selected ? 'font-semibold' : ''}`}
    data-testid={testId}>
    <span className="flex-1 min-w-0">
      <span className="block truncate"><Highlight text={ing.nombre_justo} toks={toks} /></span>
      <span className="block truncate font-mono text-[10px] text-slate-400"><Highlight text={ing.id_justo} toks={toks} /></span>
    </span>
    <span className="text-xs font-semibold text-slate-500 uppercase shrink-0">{ing.unidad}</span>
  </button>
);

export const IngredientePicker = ({ catalogo, value, onChange, disabled, testId, nombreTupu, onTab }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const activeRef = useRef(null);
  const sel = catalogo.find((i) => i.id_justo === value);
  const toks = useMemo(() => tokens(q), [q]);
  const sugeridos = useMemo(() => (open && !q ? suggest(nombreTupu, catalogo) : []), [open, q, nombreTupu, catalogo]);
  const results = useMemo(() => catalogo.filter((i) => matches(i, toks)), [catalogo, toks]);
  const all = useMemo(() => [...sugeridos, ...results], [sugeridos, results]);

  useEffect(() => { if (open) { setQ(''); setActive(0); } }, [open]);
  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => { activeRef.current?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const pick = (ing) => { onChange(ing); setOpen(false); };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, all.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (all[active]) pick(all[active]); }
    else if (e.key === 'Tab') { e.preventDefault(); if (q && all[active]) onChange(all[active]); setOpen(false); setTimeout(() => onTab && onTab(), 0); }
  };
  const opt = (ing, idx, prefix) => (
    <Option key={`${prefix}-${ing.id_justo}`} ing={ing} toks={toks} active={idx === active} selected={ing.id_justo === value}
      onPick={pick} onHover={() => setActive(idx)} optRef={idx === active ? activeRef : null} testId={`${prefix}-option-${ing.id_justo}`} />
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled}
          className="h-9 w-full min-w-[240px] rounded-md border px-2 text-sm flex items-center justify-between gap-2 bg-white text-left disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-300"
          data-testid={testId}>
          <span className={`truncate ${sel ? 'text-slate-800' : 'text-slate-400'}`}>{sel ? sel.nombre_justo : 'Elegir ingrediente Justo...'}</span>
          {sel && <span className="text-[11px] font-semibold text-slate-500 uppercase shrink-0">{sel.unidad}</span>}
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[420px]" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
          placeholder="Buscar por nombre o ID (ej. coca zero 350)" className="w-full h-10 px-3 text-sm border-b outline-none"
          data-testid={`${testId}-search`} />
        <div className="max-h-80 overflow-y-auto py-1" data-testid={`${testId}-list`}>
          {sugeridos.length > 0 && (
            <>
              <div className="px-3 pt-1 pb-0.5 text-[11px] font-semibold text-orange-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Sugeridos para “{nombreTupu}”</div>
              {sugeridos.map((ing, i) => opt(ing, i, 'sugerido'))}
              <div className="border-t my-1" />
            </>
          )}
          <div className="px-3 pb-0.5 text-[11px] text-slate-400" data-testid={`${testId}-count`}>{results.length} de {catalogo.length} ingredientes</div>
          {results.length === 0 && <p className="px-3 py-3 text-sm text-slate-500">Sin resultados. Importa el ingrediente desde la plantilla de Justo.</p>}
          {results.map((ing, i) => opt(ing, i + sugeridos.length, 'picker'))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
